import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { LookerFolder } from 'lumen/models';
import apiAction from 'lumen/utils/api-action';
import type { SingleResponse } from 'restapi/response';

class LookerDashboard extends Model {
  [Type] = 'looker-dashboard' as const;

  @belongsTo('looker-folder', { async: false, inverse: 'dashboards' })
  declare folder: LookerFolder;

  @attr('string') declare description: string;
  @attr('string') declare title: string;
  @attr('dayjs') declare updatedAt: DayjsTransformReturn;
  @attr('number') declare viewCount: number;

  @service declare store: Services['store'];

  declare copy: (options?: unknown) => Promise<unknown>;
  declare move: (options?: unknown) => Promise<unknown>;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get thumbnailUrl() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.store.adapterFor('looker-dashboard').urlForThumbnail(this.id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get updateTimestamp() {
    return this.updatedAt ? dayjs.utc(this.updatedAt).fromNow() : null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildDownloadURL(
    exportFormat: 'pdf' | 'png' | 'csv' | 'jpg',
    params?: Record<string, string>,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store
        .adapterFor('looker-dashboard')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .urlForDownload(this.id, exportFormat, params)
    );
  }
}

LookerDashboard.prototype.copy = apiAction({
  after(response: SingleResponse<LookerDashboard>) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const serializer = this.store.serializerFor('looker-dashboard');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const normalized = serializer.normalizeSingleResponse(
      this.store,
      LookerDashboard,
      response,
      Object.prototype.hasOwnProperty.call(response, 'dashboard')
        ? (response['dashboard']?.id ?? '')
        : '',
      'createRecord',
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return this.store.push(normalized);
  },
  path: 'copy',
  type: 'POST',
  urlType: 'updateRecord',
});

LookerDashboard.prototype.move = apiAction({
  after(response: SingleResponse<LookerDashboard>) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const serializer = this.store.serializerFor('looker-dashboard');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const normalized = serializer.normalizeSingleResponse(
      this.store,
      LookerDashboard,
      response,
      Object.prototype.hasOwnProperty.call(response, 'dashboard')
        ? (response['dashboard']?.id ?? '')
        : '',
      'updateRecord',
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return this.store.push(normalized);
  },
  path: 'move',
  type: 'PUT',
  urlType: 'updateRecord',
});

export default LookerDashboard;
